import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeRequest } from 'services/request.service';
// import { BeakerIcon, BugAntIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid'

import 'styles/TunnelComponent.css';

const getMatchingRows = (rows, tunnelNumber, rowIndex) => {
  const suffix = `T${tunnelNumber}-R${rowIndex + 1}`;
  const matches = rows.filter(row => row.rowId?.endsWith(suffix));

  return {
    subA: matches.find(row => row.rowId.includes('SUB_A')),
    subB: matches.find(row => row.rowId.includes('SUB_B')),
    soil: matches.find(row => !row.rowId.includes('SUB_'))
  };
};

const getWindowLabel = (i, leftCount) => {
  if (i === leftCount) return 'A';
  return `W${i < leftCount ? leftCount - i : i - leftCount}`;
};

const AccessWindowContent = ({ subARow, subBRow, soilRow }) => (
  <>
    {subARow && <div className="text-[10px] text-green-700">SUB_A: {subARow.numberOfPlants}</div>}A
    {subBRow && <div className="text-[10px] text-blue-700">SUB_B: {subBRow.numberOfPlants}</div>}
    {soilRow && <div className="text-[10px] text-gray-700">Plants: {soilRow.numberOfPlants}</div>}
  </>
);

AccessWindowContent.propTypes = {
  subARow: PropTypes.object,
  subBRow: PropTypes.object,
  soilRow: PropTypes.object
};

const renderWindowCell = ({
  rowIndex,
  windowIndex,
  isAccessWindow,
  subARow,
  subBRow,
  soilRow,
  windowLabel,
  status,
  handleDragOver,
  handleDrop
}) => {
  const statusClass = isAccessWindow ? 'access' : status;

  return (
    <div
      key={`window-${rowIndex}-${windowIndex}`}
      className={`window relative ${statusClass} level-number`}
      onDragOver={handleDragOver}
      onDrop={e => handleDrop(e, rowIndex, windowIndex)}
    >
      <div className="text-sm font-medium text-center leading-tight">
        {isAccessWindow ? (
          <AccessWindowContent subARow={subARow} subBRow={subBRow} soilRow={soilRow} />
        ) : (
          windowLabel
        )}
      </div>
    </div>
  );
};

const TunnelComponent = ({
  tunnelNumber,
  configType = 'default',
  numberOfWindows = 16,
  isHydroponicBlock,
  accessWindow,
  toggleIsTunnelRowModalOpen,
  farmingBlockId,
  onExposeRefresh
}) => {
  const hasFetched = useRef(false);
  const [windowStates, setWindowStates] = useState({});
  const [tunnelRowsByBlock, setTunnelRowData] = useState([]);

  let numberOfRows = isHydroponicBlock ? 6 : 9;
  if (tunnelNumber === 1 && !isHydroponicBlock) {
    numberOfRows = 8;
  }

  // const handleDragStart = (e, status) => {
  //   e.dataTransfer.setData('status', status)
  // }

  const handleDragOver = e => {
    e.preventDefault();
  };

  const handleDrop = (e, rowIndex, windowIndex) => {
    if (accessWindow === windowIndex + 1) return;
    e.preventDefault();
    const status = e.dataTransfer.getData('status');
    setWindowStates(prev => ({
      ...prev,
      [`${rowIndex}-${windowIndex}`]: status
    }));
  };

  // const handleReset = () => {
  //   setWindowStates({})
  // }

  const onRowHeaderClick = (tunnelNumber, rowIndex) => {
    const rowSuffix = `T${tunnelNumber}-R${rowIndex + 1}`;
    const matchingRows = tunnelRowsByBlock.filter(row => row.rowId?.endsWith(rowSuffix));

    const subARow = matchingRows.find(row => row.rowId.includes('SUB_A'));
    const subBRow = matchingRows.find(row => row.rowId.includes('SUB_B'));
    const soilRow = matchingRows.find(row => !row.rowId.includes('SUB_'));

    let initialData;

    if (subARow || subBRow) {
      initialData = {
        rowId: subARow?.rowId || subBRow?.rowId,
        subA: {
          cropId: subARow?.cropId || '',
          varietyId: subARow?.varietyId || '',
          numberOfPlants: subARow?.numberOfPlants || '',
          rowId: subARow?.rowId || ''
        },
        subB: {
          cropId: subBRow?.cropId || '',
          varietyId: subBRow?.varietyId || '',
          numberOfPlants: subBRow?.numberOfPlants || '',
          rowId: subBRow?.rowId || ''
        }
      };
    } else if (soilRow) {
      initialData = {
        cropId: soilRow.cropId || '',
        varietyId: soilRow.varietyId || '',
        numberOfPlants: soilRow.numberOfPlants || '',
        rowId: soilRow.rowId || ''
      };
    } else {
      initialData = {};
    }

    toggleIsTunnelRowModalOpen(rowIndex, initialData);
  };

  const fetchTunnelRowsByBlockId = async farmingBlockId => {
    const response = await makeRequest({
      method: 'GET',
      url: `/management/farming-blocks/${farmingBlockId}/rows`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    });

    setTunnelRowData(Array.isArray(response.data) ? response.data : []);
  };

  useEffect(() => {
    if (!farmingBlockId) return;

    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchTunnelRowsByBlockId(farmingBlockId);
  }, [farmingBlockId]);

  useEffect(() => {
    if (typeof onExposeRefresh === 'function') {
      onExposeRefresh(() => fetchTunnelRowsByBlockId(farmingBlockId));
    }
  }, [farmingBlockId, onExposeRefresh]);

  return (
    <div className={`tunnel ${configType}`}>
      <div className="tunnel-header" />

      {/* <div className='status-markers'>
        <div className='markers-container'>
          <div
            className='status-marker healthy'
            draggable
            onDragStart={(e) => handleDragStart(e, 'healthy')}
          >
            <CheckCircleIcon style={{ width: 20, height: 20 }} /> Healthy
          </div>
          <div
            className='status-marker disease'
            draggable
            onDragStart={(e) => handleDragStart(e, 'disease')}
          >
            <ExclamationTriangleIcon style={{ width: 20, height: 20 }} /> Disease
          </div>
          <div
            className='status-marker nutrient-deficiency'
            draggable
            onDragStart={(e) => handleDragStart(e, 'nutrient-deficiency')}
          >
            <BeakerIcon style={{ width: 20, height: 20 }} /> Nutrient Deficiency
          </div>
          <div
            className='status-marker plague'
            draggable
            onDragStart={(e) => handleDragStart(e, 'plague')}
          >
            <BugAntIcon style={{ width: 20, height: 20 }} /> Plague
          </div>
        </div>
        <button
          className='reset-status-button'
          onClick={handleReset}
          title='Reset all windows'
        >
          Reset All
        </button>
      </div> */}

      <div className="tunnel-grid-container">
        <div className="tunnel-grid">
          <div className="level-numbers">
            {Array.from({ length: numberOfRows }, (_, i) => (
              <div
                key={`level-${i}`}
                className="level-number"
                onClick={() => onRowHeaderClick(tunnelNumber, i)}
              >
                R{i + 1}
              </div>
            ))}
          </div>

          <div className="grid-lines">
            {Array.from({ length: numberOfRows }, (_, rowIndex) => {
              const {
                subA: subARow,
                subB: subBRow,
                soil: soilRow
              } = getMatchingRows(tunnelRowsByBlock, tunnelNumber, rowIndex);

              const allIndexes = Array.from({ length: numberOfWindows }, (_, i) => i);
              const accessIndex = accessWindow - 1;

              const left = allIndexes.slice(0, accessIndex).reverse();
              const right = allIndexes.slice(accessIndex + 1);
              const orderedIndexes = [...left, accessIndex, ...right];

              return (
                <div key={`row-${rowIndex}`} className="grid-row">
                  {orderedIndexes.map((windowIndex, i) => {
                    const isAccessWindow = windowIndex === accessIndex;
                    const windowLabel = getWindowLabel(i, left.length);
                    const status = windowStates[`${rowIndex}-${windowIndex}`] || 'healthy';

                    return renderWindowCell({
                      rowIndex,
                      windowIndex,
                      isAccessWindow,
                      subARow,
                      subBRow,
                      soilRow,
                      windowLabel,
                      status,
                      handleDragOver,
                      handleDrop
                    });
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

TunnelComponent.propTypes = {
  tunnelNumber: PropTypes.number.isRequired,
  accessWindow: PropTypes.number.isRequired,
  farmingBlockId: PropTypes.number.isRequired,
  toggleIsTunnelRowModalOpen: PropTypes.func.isRequired,
  configType: PropTypes.oneOf(['default', 'custom']),
  numberOfWindows: PropTypes.number,
  isHydroponicBlock: PropTypes.bool,
  onExposeRefresh: PropTypes.func
};

export default TunnelComponent;
